import { I18n } from "@assets/strings";
import { Set } from "immutable";
import React, { useState } from "react";
import styled from "styled-components";
import { Environment } from "@core/config/environment";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { PlusIconRound } from "@ui/components/icons";
import { checkboxInputCss } from "@ui/components/inputs/checkboxInput";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { stack } from "@ui/components/layout";

interface TrackLevelsEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	levels: Set<string>;
	setLevels: (levels: Set<string>) => void;
}

export const TrackLevelsEditor: React.FC<TrackLevelsEditorProps> = ({ levels, setLevels, ...htmlProps }) => {
	const [newLevelValue, setNewLevelValue] = useState("");
	const [checkableLevels, setCheckableLevels] = useState(
		levels.size > 0
			? levels
			: Set(
					Environment.APP_VARIANT === "jaangal"
						? [I18n.t("track.level.baccalaureat"), I18n.t("track.level.bepc")]
						: [I18n.t("track.level.phd"), I18n.t("track.level.mba")]
				)
	);

	const addValue = (): void => {
		if (!newLevelValue || checkableLevels.find((level) => level.toLowerCase() === newLevelValue)) {
			return;
		}
		setCheckableLevels(checkableLevels.add(newLevelValue));
		setLevels(levels.add(newLevelValue));
		setNewLevelValue("");
	};

	return (
		<Container {...htmlProps}>
			<PrimaryMedium>{I18n.t("organization.track.levels.label")}</PrimaryMedium>
			{checkableLevels.map((level) => (
				<LevelChoice key={level}>
					<input
						id={level}
						name={level}
						type="checkbox"
						checked={levels.has(level)}
						onChange={(e) => setLevels(e.currentTarget.checked ? levels.add(level) : levels.remove(level))}
					/>
					<label htmlFor={level}>{level}</label>
				</LevelChoice>
			))}
			<SimpleTextInput
				placeholder={I18n.t("organization.track.levels.placeholder")}
				value={newLevelValue}
				onChange={setNewLevelValue}
				onSubmit={addValue}
				icon={<PlusIconRound onClick={addValue} />}
			/>
		</Container>
	);
};

const Container = styled.div`
	${stack(10)};
`;

const LevelChoice = styled.div`
	display: flex;
	align-items: center;

	input {
		${checkboxInputCss};
		margin-right: 10px;
	}

	label {
		${({ theme }) => theme.textStyles.primary};
	}
`;
